import { getManagedTeam, isBranchManager } from "./branchScope";

export const COMPANY_GMAIL_SCOPE = "company";

/** HARD allowlist — must match backend gmailInbox/lib/authCheck.js */
export const GMAIL_INTEGRATION_ALLOWLIST = new Set([
  "info@flex.al",
  "lorin@flex.al",
]);

const COMPANY_GMAIL_EMAIL = "info@flex.al";
const BRANCH_GMAIL_EMAIL = "lorin@flex.al";

export function normalizeGmailEmail(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

/** Resolve login email from auth context or a raw user row. */
export function resolveAuthEmail(authOrUser) {
  if (!authOrUser) return "";
  // useAuth() shape
  if (authOrUser.user || authOrUser.userAccess) {
    return normalizeGmailEmail(
      authOrUser.user?.email ||
        authOrUser.userAccess?.[0]?.email ||
        authOrUser.employee?.employeeEmail
    );
  }
  // plain user row
  return normalizeGmailEmail(authOrUser.email);
}

/**
 * True only for info@flex.al / lorin@flex.al.
 * Admin / Super Admin / Branch Manager roles alone are NOT enough.
 */
export function canAccessGmailIntegration(authOrUser) {
  return GMAIL_INTEGRATION_ALLOWLIST.has(resolveAuthEmail(authOrUser));
}

/**
 * Resolve which Gmail inbox scope the current auth user may access.
 * - info@flex.al → company
 * - lorin@flex.al → team:<managedTeam>
 * - everyone else → null (no access)
 */
export function resolveGmailScopeId(auth) {
  if (!canAccessGmailIntegration(auth)) return null;

  const email = resolveAuthEmail(auth);
  if (email === BRANCH_GMAIL_EMAIL || isBranchManager(auth)) {
    const team = getManagedTeam(auth) || "Flex Prishtina";
    return `team:${team}`;
  }
  if (email === COMPANY_GMAIL_EMAIL) {
    return COMPANY_GMAIL_SCOPE;
  }
  return null;
}

/** Amplify API options that attach scopeId as query string. */
export function gmailScopeQuery(auth) {
  const scopeId = resolveGmailScopeId(auth);
  if (!scopeId) return {};
  return { queryStringParameters: { scopeId } };
}

/** Merge scopeId into a request body. */
export function withGmailScope(body, auth) {
  const scopeId = resolveGmailScopeId(auth);
  return { ...(body || {}), ...(scopeId ? { scopeId } : {}) };
}

/** Remove Gmail / Integrime entries from routeConfig for non-allowlisted users. */
export function stripGmailRouteAccess(routeConfig = []) {
  return (Array.isArray(routeConfig) ? routeConfig : []).filter(
    (c) => !String(c?.path || "").includes("/konfigurimet/integrime/gmail")
  );
}

/** Remove Integrime / Gmail from settingsConfig for non-allowlisted users. */
export function stripGmailSettingsAccess(settingsConfig = []) {
  return (Array.isArray(settingsConfig) ? settingsConfig : [])
    .map((section) => {
      if (section?.SubmenuKey === "subIntegrime") return null;
      const submenu = Array.isArray(section?.submenu)
        ? section.submenu.filter(
            (row) => !String(row?.to || "").includes("integrime/gmail")
          )
        : section?.submenu;
      return { ...section, submenu };
    })
    .filter(Boolean);
}
